import { Controller } from "@hotwired/stimulus"
import { useWindowResize } from "stimulus-use"

// Duration of the glow's fade-out (see `.ui-sidebar-glow` in sidebar.css). We
// hold the glow hidden this long before repositioning and fading it back in,
// so tab-to-tab navigation reads as a clean fade-out then fade-in.
const FADE_OUT_MS = 500

// Positions the ambient gradient glow behind the active sidebar tab. The glow
// element is `data-turbo-permanent`, so a single node survives Turbo
// navigations instead of being torn down and recreated — which made it cut off
// abruptly mid-navigation. On each navigation the glow fades out at its old
// position, then reappears behind the newly active tab.
//
// Anchoring runs from `fieldTargetConnected`, not `connect`: on a Turbo visit
// the controller's element (#sidebar) is rebuilt and reconnects before Turbo
// relocates the permanent glow into it, so the target isn't in scope yet at
// `connect` time. `fieldTargetConnected` fires once the glow lands.
export default class extends Controller {
  static targets = ["field"]

  connect() {
    useWindowResize(this)
  }

  disconnect() {
    clearTimeout(this.fadeTimer)
  }

  fieldTargetConnected() {
    this.#anchor()
  }

  windowResize() {
    // Track the active tab live, without the fade cycle.
    if (this.hasFieldTarget && this.fieldTarget.classList.contains("is-visible")) {
      this.#position()
    }
  }

  #anchor() {
    if (!this.#activeTab) {
      this.fieldTarget.classList.remove("is-visible")
      return
    }

    // Fresh page load: the glow starts hidden, so just position and fade in.
    if (!this.fieldTarget.classList.contains("is-visible")) {
      this.#position()
      this.fieldTarget.classList.add("is-visible")
      return
    }

    // Turbo navigation: the persisted glow is still visible at its previous
    // position. The node was just relocated into the freshly rendered #sidebar,
    // so force a reflow to lock in its current opacity as the transition's
    // starting value — otherwise removing `is-visible` snaps straight to 0
    // instead of fading out. Then reposition and fade it back in.
    void this.fieldTarget.offsetWidth
    this.fieldTarget.classList.remove("is-visible")
    this.fadeTimer = setTimeout(() => {
      this.#position()
      this.fieldTarget.classList.add("is-visible")
    }, FADE_OUT_MS)
  }

  #position() {
    if (!this.#activeTab) return

    const containerTop = this.element.getBoundingClientRect().top
    const { top, height } = this.#activeTab.getBoundingClientRect()

    this.fieldTarget.style.setProperty("--glow-top", `${top - containerTop + height / 2}px`)
  }

  get #activeTab() {
    return this.element.querySelector(".ui-sidebar-tab.active")
  }
}
